
/**
 * Utilitaires pour les assessments
 * Gestion des scores, couleurs et niveaux de maturité
 */

export interface ScoreLevel {
  level: string;
  color: string;
  icon: string;
  description: string;
}

/**
 * Détermine le niveau de maturité basé sur le score
 */
export const getScoreLevel = (score: number): ScoreLevel => {
  if (score >= 81) return {
    level: "Leader",
    color: "#059669",
    icon: "",
    description: "Référence IA. Bravo ! Vous êtes une source d'inspiration pour les autres."
  };
  if (score >= 61) return {
    level: "Avancé",
    color: "#10B981",
    icon: "",
    description: "Maturité affirmée. Vous avez une vision claire, menez des actions concrètes"
  };
  if (score >= 41) return {
    level: "Intermédiaire",
    color: "#F59E0B",
    icon: "",
    description: "En phase d'accélération. Vous êtes actif sur le sujet et avez déjà décollé."
  };
  if (score >= 21) return {
    level: "Émergent",
    color: "#F5570B",
    icon: "",
    description: "En structuration. De premières briques déjà sont posées."
  };
  return {
    level: "Débutant",
    color: "#EF4444",
    icon: "",
    description: "En phase d'exploration. Vous débutez, mais l'intérêt est présent."
  };
};

/**
 * Couleur en fonction du score (pour les cartes de catégories)
 */
export const getScoreColor = (score: number): string => {
  if (score >= 81) return "#059669";
  if (score >= 61) return "#10B981";
  if (score >= 41) return "#F59E0B";
  if (score >= 21) return "#F5570B";
  return "#EF4444";
};

/**
 * Calcule le score global à partir des scores de catégories
 */
export const calculateOverallScore = (scores: Record<string, { score: number; maxScore: number }>): number => {
  const categoryScores = Object.entries(scores)
    .filter(([category]) => category !== 'Profil entreprise')
    .map(([, data]) => data)
    .filter(({ score, maxScore }) => Number.isFinite(score) && Number.isFinite(maxScore) && maxScore > 0);

  if (categoryScores.length === 0) return 0;
  
  return Math.round(
    categoryScores.reduce((acc, data) => acc + (data.score / data.maxScore), 0) / categoryScores.length * 100
  );
};

/**
 * Filtre les catégories scorées (exclut "Profil entreprise")
 */
export const getScoredCategories = (scores: Record<string, { score: number; maxScore: number }>) => {
  return Object.entries(scores)
    .filter(([category]) => category !== "Profil entreprise")
    .map(([category, data]) => ({
      category,
      percentage:
        Number.isFinite(data.score) && Number.isFinite(data.maxScore) && data.maxScore > 0
          ? Math.round((data.score / data.maxScore) * 100)
          : 0,
      score: data.score,
      maxScore: data.maxScore
    }));
};

/**
 * Configuration des couleurs de la charte graphique Nokodia
 */
export const NOKODIA_COLORS = {
  primary: '#D7398B', // Rose principal
  secondary: '#FD41A6', // Rose secondaire  
  accent1: '#E74810', // Orange principal
  accent2: '#FF6600', // Orange secondaire
  yellow: '#FFBF25',
  blue: '#43B4E8',
  gradient: 'linear-gradient(135deg, #FD41A6, #FF6600)', // Gradient principal rose -> orange
} as const;

/**
 * Vérifie si on peut procéder à l'étape suivante pour l'assessment digital transformation
 */
export const canProceedDigitalTransformation = (
  currentStep: number,
  totalSteps: number,
  profileQuestions: any[],
  questionsByCategory: Record<string, any[]>,
  categories: string[],
  answers: Record<string, any>,
  contactInfo: any
): boolean => {
  // Étape 0 (intro): toujours autorisé
  if (currentStep === 0) return true;
  
  // Étape 1 (profil): vérifier que toutes les questions profil ont une réponse
  if (currentStep === 1) {
    return profileQuestions.every(q => answers[q.id] !== undefined);
  }
  
  // Étapes 2 à 2+categories.length-1 (questions par catégorie)
  if (currentStep >= 2 && currentStep < 2 + categories.length) {
    const categoryIndex = currentStep - 2;
    const category = categories[categoryIndex];
    const categoryQuestions = questionsByCategory[category] || [];
    return categoryQuestions.every(q => answers[q.id] !== undefined);
  }
  
  // Étape contact (totalSteps - 2): vérifier les infos de contact
  if (currentStep === totalSteps - 2) {
    return contactInfo?.firstName && 
           contactInfo?.lastName && 
           contactInfo?.email && 
           contactInfo?.rgpdConsent;
  }
  
  // Étape résultats (totalSteps - 1): toujours autorisé
  return true;
};

// =============================================================================
// TRAINING TRANSFORMATION (43 questions, 9 axes pondérés, 3 paliers)
// =============================================================================

export const TRAINING_AXIS_WEIGHTS: Record<string, number> = {
  'Stratégie': 15,
  'Offre': 12,
  'Commercial': 10,
  'Organisation': 10,
  'Processus': 10,
  'Ingénierie': 15,
  'Qualité': 8,
  'Outils': 12,
  'Change': 8,
};

/**
 * Couleur du palier Training Transformation par % (0-100).
 * Cohérent avec getTrainingScoreLevel.
 */
export const getTrainingScoreColor = (percentage: number): string => {
  if (percentage >= 81) return '#059669';
  if (percentage >= 61) return '#10B981';
  if (percentage >= 41) return '#F59E0B';
  if (percentage >= 21) return '#F5570B';
  return '#EF4444';
};

/**
 * 5 paliers du diagnostic Training Transformation IA, alignés sur le diagnostic IA.
 */
export const getTrainingScoreLevel = (score: number): ScoreLevel => {
  if (score >= 81) return {
    level: "Leader",
    color: "#059669",
    icon: "",
    description: "Référence IA. Bravo ! Vous êtes une source d'inspiration pour les autres.",
  };
  if (score >= 61) return {
    level: "Avancé",
    color: "#10B981",
    icon: "",
    description: "Maturité affirmée. Vous avez une vision claire, menez des actions concrètes",
  };
  if (score >= 41) return {
    level: "Intermédiaire",
    color: "#F59E0B",
    icon: "",
    description: "En phase d'accélération. Vous êtes actif sur le sujet et avez déjà décollé.",
  };
  if (score >= 21) return {
    level: "Émergent",
    color: "#F5570B",
    icon: "",
    description: "En structuration. De premières briques déjà sont posées.",
  };
  return {
    level: "Débutant",
    color: "#EF4444",
    icon: "",
    description: "En phase d'exploration. Vous débutez, mais l'intérêt est présent.",
  };
};

/**
 * Score global pondéré (0-100) à partir des scores par axe et de leurs poids.
 *
 * @param categoryScores  Map { axisName: { score, maxScore } } (uniquement axes scorés)
 * @param weights         Map { axisName: weight } — somme des poids attendue ≈ 100
 */
export const calculateWeightedOverallScore = (
  categoryScores: Record<string, { score: number; maxScore: number }>,
  weights: Record<string, number>,
): number => {
  let totalWeight = 0;
  let weightedSum = 0;

  Object.entries(categoryScores).forEach(([axis, { score, maxScore }]) => {
    if (axis === 'Profil entreprise') return;
    const w = weights[axis];
    if (!w || maxScore <= 0) return;
    const ratio = score / maxScore; // 0..1
    weightedSum += ratio * w;
    totalWeight += w;
  });

  if (totalWeight === 0) return 0;
  // Normalise sur 100, indépendamment de la somme exacte des poids
  return Math.round((weightedSum / totalWeight) * 100);
};

/**
 * Source de vérité frontend du score global : moyenne simple pour le diagnostic IA,
 * pondération par axe pour le diagnostic Formation.
 */
export const calculateAssessmentOverallScore = (
  scores: Record<string, { score: number; maxScore: number }>,
  assessmentType: 'ia-diagnostic' | 'training-transformation' = 'ia-diagnostic',
): number =>
  assessmentType === 'training-transformation'
    ? calculateWeightedOverallScore(scores, TRAINING_AXIS_WEIGHTS)
    : calculateOverallScore(scores);
